import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePatient } from "../../contexts";
import Cookies from "js-cookie";
import { numberWithCommas } from "../../utility/general";
import { AiOutlinePlus } from "react-icons/ai";
import { RiDeleteBinLine } from "react-icons/ri";
import MetricModal from "../modals/MetricModal";
import TableSkeleton from "./TableSkeleton";
import { HiPencil } from "react-icons/hi";
import { del } from "../../utility/fetch";
import notification from "../../utility/notification";
import DeleteConfirmationModal from "../modals/DeleteConfirmation";

function CostSetupTable({
  data,
  setEditDetails,
  currentPage,
  loading,
  getInternalLabService,
  itemsPerPage,
}) {
  const {
    setPatientId,
    setPatientName,
    setHmoId,
    setPatientInfo,
    setHmoDetails,
  } = usePatient();

  const [metricModal, setMetricModal] = useState(false);
  const [metricData, setMetricData] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;

  const handleDeleteConfirmationModal = (row, item) => {
    setDeleteRow(row);
    setDeleteItem(item);
    setOpenDeleteModal(true);
  };

  const handleDeleteTestDetails = async (row, item) => {
    const res = await del(`/internallabservice/${row.id}/test-details/${item.id}`);
    if (res?.message) {
      setOpenDeleteModal(false);
      notification({ message: res.message, type: "success" });
      getInternalLabService();
    } else {
      notification({ message: "Error deleting test detail", type: "error" });
    }
  };

  const addMetric = (row) => {
    setMetricData(row);
    setMetricModal(true);
  };

  /* ================= MOBILE CARD VIEW ================= */
  if (isMobile) {
    return (
      <div className="flex flex-col gap-12">
        {loading ? (
          <TableSkeleton />
        ) : (
          data?.map((row) => (
            <div key={row.id} className="card shadow-sm m-b-20" style={{ borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
              
              {/* Header */}
              <div className="flex flex-h-between flex-v-center" style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                <h4 className="m-0" style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>{row?.name}</h4>
                <div className="flex flex-v-center">
                   <div className="pointer m-r-15" onClick={() => addMetric(row)} title="Add Metric">
                      <AiOutlinePlus size={18} color="#28a745" />
                   </div>
                   <div className="pointer" onClick={() => setEditDetails(row)} title="Edit Test">
                      <HiPencil size={18} color="#007bff" />
                   </div>
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: '15px' }}>
                
                {/* Info Row */}
                <div className="flex flex-h-between m-b-15">
                    <div className="flex flex-col">
                        <span className="text-uppercase text-muted" style={{ fontSize: '11px', fontWeight: 'bold', color: '#888', marginBottom: '4px' }}>COST</span>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>₦{numberWithCommas(row?.cost)}</span>
                    </div>
                    <div className="flex flex-col text-right">
                        <span className="text-uppercase text-muted" style={{ fontSize: '11px', fontWeight: 'bold', color: '#888', marginBottom: '4px' }}>CREATED</span>
                        <span style={{ fontSize: '14px', color: '#555' }}>{new Date(row?.createdOn).toLocaleDateString()}</span>
                    </div>
                </div>

                {/* Description */}
                <div className="m-b-15">
                    <span className="text-uppercase text-muted block m-b-5" style={{ fontSize: '11px', fontWeight: 'bold', color: '#888', display: 'block' }}>DESCRIPTION</span>
                    <p className="m-0" style={{ fontSize: '14px', color: '#555', lineHeight: '1.5' }}>{row?.description || 'No description provided.'}</p>
                </div>

                {/* Metrics */}
                <div>
                    <span className="text-uppercase text-muted block m-b-8" style={{ fontSize: '11px', fontWeight: 'bold', color: '#888', display: 'block' }}>METRICS ({row?.labServiceTestDetails?.length || 0})</span>
                    <div className="flex flex-wrap" style={{ gap: '8px' }}>
                        {row?.labServiceTestDetails?.length > 0 ? row?.labServiceTestDetails?.map((item, idx) => (
                            <div key={item.id} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', backgroundColor: '#e9ecef', borderRadius: '20px', fontSize: '13px', color: '#495057' }}>
                                <span>{item.parameter}</span>
                                <RiDeleteBinLine
                                    size={14}
                                    color="#dc3545"
                                    className="pointer m-l-8"
                                    onClick={() => handleDeleteConfirmationModal(row, item)}
                                />
                            </div>
                        )) : <span className="italic text-muted" style={{ fontSize: '13px' }}>No metrics added.</span>}
                    </div>
                </div>

              </div>
              
              {/* Footer */}
              <div style={{ padding: '10px 15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #eee', fontSize: '12px', color: '#6c757d' }}>
                  Created by: <strong>{row?.createdBy?.firstName} {row?.createdBy?.lastName}</strong>
              </div>
            </div>
          ))
        )}

        {openDeleteModal && (
          <DeleteConfirmationModal
            closeModal={() => setOpenDeleteModal(false)}
            confirmDelete={() =>
              handleDeleteTestDetails(deleteRow, deleteItem)
            }
            equipment="labTestDetails"
          />
        )}

        {metricModal && (
          <MetricModal
            getInternalLabService={getInternalLabService}
            data={metricData}
            closeModal={() => setMetricModal(false)}
          />
        )}
      </div>
    );
  }

  /* ================= DESKTOP TABLE VIEW ================= */
  return (
    <div className="w-100 m-t-40">
      <table className="bordered-table">
        <thead>
          <tr>
            <th>Test</th>
            <th>Description</th>
            <th>Cost</th>
            <th>Metrics</th>
            <th>Created By</th>
            <th>Created Date</th>
            <th></th>
            <th></th>
          </tr>
        </thead>

        {loading ? (
          <TableSkeleton />
        ) : (
          <tbody>
            {data?.map((row) => (
              <tr key={row.id} className="hovers">
                <td>{row?.name}</td>
                <td>{row?.description}</td>
                <td>₦{numberWithCommas(row?.cost)}</td>
                <td>
                  {row?.labServiceTestDetails?.map((item, idx) => (
                    <span key={item.id} className="m-r-10">
                      {idx + 1}. {item.parameter}
                      <RiDeleteBinLine
                        size={15}
                        color="red"
                        className="pointer m-l-4"
                        onClick={() =>
                          handleDeleteConfirmationModal(row, item)
                        }
                      />
                    </span>
                  ))}
                </td>
                <td>
                  {row?.createdBy?.firstName} {row?.createdBy?.lastName}
                </td>
                <td>{new Date(row?.createdOn).toLocaleDateString()}</td>
                <td>
                  <AiOutlinePlus
                    size={22}
                    className="pointer"
                    onClick={() => addMetric(row)}
                  />
                </td>
                <td>
                  <HiPencil
                    size={22}
                    className="pointer"
                    onClick={() => setEditDetails(row)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>

      {openDeleteModal && (
        <DeleteConfirmationModal
          closeModal={() => setOpenDeleteModal(false)}
          confirmDelete={() =>
            handleDeleteTestDetails(deleteRow, deleteItem)
          }
          equipment="labTestDetails"
        />
      )}

      {metricModal && (
        <MetricModal
          getInternalLabService={getInternalLabService}
          data={metricData}
          closeModal={() => setMetricModal(false)}
        />
      )}
    </div>
  );
}

export default CostSetupTable;
